// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
const IMAGE_REQUIREMENTS_COPY =
  'PNG only, any dimensions and file size (within your Firebase Storage limits). File name: letters, numbers, dots, dashes, underscores, ending in .png';

function EventGridThumbnail({ name, url, resolveError }) {
  const [failed, setFailed] = useState(false);

  const handleImageError = useCallback(() => {
    setFailed(true);
  }, []);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <Box
        sx={{
          aspectRatio: '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          p: 1,
          gap: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center">
          {url ? 'Could not load preview' : 'Preview unavailable'}
        </Typography>
        {resolveError && (
          <Typography
            variant="caption"
            color="error"
            align="center"
            sx={{ wordBreak: 'break-word', fontSize: '0.65rem' }}
          >
            {resolveError}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt={name}
      referrerPolicy="no-referrer"
      onError={handleImageError}
      sx={{
        aspectRatio: '1',
        width: '100%',
        display: 'block',
        objectFit: 'contain',
        bgcolor: 'action.hover',
        p: 1,
        boxSizing: 'border-box',
      }}
    />
  );
}

function EventImageFileCard({ file, onRequestDelete }) {
  const handleDeleteClick = useCallback(() => {
    onRequestDelete(file.name);
  }, [file.name, onRequestDelete]);

  return (
    <Card variant="outlined" sx={{ position: 'relative' }}>
      <IconButton
        size="small"
        color="error"
        aria-label={`Delete ${file.name}`}
        onClick={handleDeleteClick}
        sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      <EventGridThumbnail
        name={file.name}
        url={file.url}
        resolveError={file.resolveError}
      />
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
          {file.name}
        </Typography>
      </Box>
    </Card>
  );
}

function EventImagesFileGrid({ files, onRequestDelete }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
        },
      }}
    >
      {files.map((f) => (
        <EventImageFileCard
          key={f.name}
          file={f}
          onRequestDelete={onRequestDelete}
        />
      ))}
    </Box>
  );
}

function EventImagesUploadToolbar({
  inputRef,
  onFileChange,
  onPickFile,
  onRefresh,
  uploading,
  loading,
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        hidden
        onChange={onFileChange}
      />
      <Button
        variant="contained"
        onClick={onPickFile}
        disabled={uploading}
      >
        Add image
      </Button>
      <Button
        startIcon={<RefreshIcon />}
        onClick={onRefresh}
        disabled={loading || uploading}
      >
        Refresh
      </Button>
    </Box>
  );
}

function EventImagesDeleteDialog({
  deleteTarget,
  deleteBusy,
  deleteBlockMessage,
  onClose,
  onCancel,
  onConfirm,
}) {
  const blockBackdropClose = Boolean(deleteBlockMessage);

  return (
    <Dialog
      open={Boolean(deleteTarget)}
      onClose={onClose}
      disableEscapeKeyDown={blockBackdropClose}
    >
      <DialogTitle>Delete image?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Remove <strong>{deleteTarget}</strong> from Storage? This cannot be undone.
        </DialogContentText>
        {deleteBlockMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {deleteBlockMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={deleteBusy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={deleteBusy}
        >
          {deleteBusy ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EventImagesTabMain({
  inputRef,
  files,
  loading,
  error,
  localError,
  successMsg,
  uploading,
  uploadPercent,
  onFileChange,
  onPickFile,
  onRefresh,
  onDismissSuccess,
  onRequestDelete,
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {IMAGE_REQUIREMENTS_COPY}
      </Typography>

      <EventImagesUploadToolbar
        inputRef={inputRef}
        onFileChange={onFileChange}
        onPickFile={onPickFile}
        onRefresh={onRefresh}
        uploading={uploading}
        loading={loading}
      />

      {uploading && (
        <Box sx={{ maxWidth: 360 }}>
          <LinearProgress variant="determinate" value={uploadPercent} />
          <Typography variant="caption" color="text.secondary">
            Uploading… {uploadPercent}%
          </Typography>
        </Box>
      )}

      {(error || localError) && (
        <Alert severity="error">{localError || error}</Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={onDismissSuccess}>
          {successMsg}
        </Alert>
      )}

      {loading && !files.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <EventImagesFileGrid
          files={files}
          onRequestDelete={onRequestDelete}
        />
      )}

      {!loading && !files.length && !error && (
        <Typography variant="body2" color="text.secondary">
          No images in Storage yet. Upload a PNG using the button above.
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Admin tab: list / upload / delete event images in Storage `events/`.
 */
function EventImagesTab({
  files,
  loading,
  error,
  refresh,
  uploadFile,
  deleteImageIfUnused,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [localError, setLocalError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteBlockMessage, setDeleteBlockMessage] = useState('');
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  const [pendingOverwriteFile, setPendingOverwriteFile] = useState(null);
  const [overwriteMessage, setOverwriteMessage] = useState('');

  const handlePickFile = useCallback(() => {
    setLocalError('');
    setSuccessMsg('');
    inputRef.current?.click();
  }, []);

  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleDismissSuccess = useCallback(() => {
    setSuccessMsg('');
  }, []);

  const handleRequestDelete = useCallback((name) => {
    setDeleteBlockMessage('');
    setDeleteTarget(name);
  }, []);

  const handleDeleteDialogClose = useCallback(
    (event, reason) => {
      if (
        deleteBlockMessage &&
        (reason === 'backdropClick' || reason === 'escapeKeyDown')
      ) {
        return;
      }
      if (!deleteBusy) {
        setDeleteTarget(null);
      }
    },
    [deleteBusy, deleteBlockMessage],
  );

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleCancelOverwrite = useCallback(() => {
    setOverwriteOpen(false);
    setPendingOverwriteFile(null);
    setOverwriteMessage('');
  }, []);

  const handleConfirmOverwrite = useCallback(async () => {
    const file = pendingOverwriteFile;
    setOverwriteOpen(false);
    setPendingOverwriteFile(null);
    setOverwriteMessage('');
    if (!file) return;

    setLocalError('');
    setSuccessMsg('');
    setUploading(true);
    setUploadPercent(0);
    const again = await uploadFile(file, {
      overwrite: true,
      onProgress: setUploadPercent,
    });
    setUploading(false);
    setUploadPercent(0);
    if (!again.ok) {
      setLocalError(again.message);
      return;
    }
    setSuccessMsg(`Uploaded "${again.name}".`);
  }, [pendingOverwriteFile, uploadFile]);

  const runUpload = useCallback(
    async (file, overwrite) => {
      setLocalError('');
      setSuccessMsg('');
      setUploading(true);
      setUploadPercent(0);
      const result = await uploadFile(file, {
        overwrite,
        onProgress: setUploadPercent,
      });
      if (!result.ok && result.code === 'DUPLICATE' && !overwrite) {
        setUploading(false);
        setUploadPercent(0);
        setPendingOverwriteFile(file);
        setOverwriteMessage(result.message);
        setOverwriteOpen(true);
        return;
      }
      setUploading(false);
      setUploadPercent(0);
      if (!result.ok) {
        setLocalError(result.message);
        return;
      }
      setSuccessMsg(`Uploaded "${result.name}".`);
    },
    [uploadFile],
  );

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      await runUpload(file, false);
    },
    [runUpload],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteBlockMessage('');
    const result = await deleteImageIfUnused(deleteTarget);
    setDeleteBusy(false);
    if (!result.ok) {
      if (result.referencing?.length) {
        const names = result.referencing
          .map((r) => `"${r.name}" (${r.id})`)
          .join(', ');
        setDeleteBlockMessage(
          `This image is used by event definition(s): ${names}. Change those events to another image first.`,
        );
        return;
      }
      setDeleteBlockMessage(result.message || 'Could not delete.');
      return;
    }
    const removed = deleteTarget;
    setDeleteTarget(null);
    setSuccessMsg(`Deleted "${removed}".`);
  }, [deleteImageIfUnused, deleteTarget]);

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <EventImagesTabMain
        inputRef={inputRef}
        files={files}
        loading={loading}
        error={error}
        localError={localError}
        successMsg={successMsg}
        uploading={uploading}
        uploadPercent={uploadPercent}
        onFileChange={handleFileChange}
        onPickFile={handlePickFile}
        onRefresh={handleRefresh}
        onDismissSuccess={handleDismissSuccess}
        onRequestDelete={handleRequestDelete}
      />
      <EventImagesDeleteDialog
        deleteTarget={deleteTarget}
        deleteBusy={deleteBusy}
        deleteBlockMessage={deleteBlockMessage}
        onClose={handleDeleteDialogClose}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <Dialog open={overwriteOpen} onClose={handleCancelOverwrite}>
        <DialogTitle>Replace existing file?</DialogTitle>
        <DialogContent>
          <DialogContentText>{overwriteMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOverwrite}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmOverwrite}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default EventImagesTab;
